import { ChangeDetectorRef, Component, ComponentRef, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { BaseGridFilter, WithSubscription } from '@shared/base';
import { Column, GridFilterValue, SortDirection } from '@shared/models';
import { ColumnFilterType } from '@shared/models/grid-column.model';
import { GridDaterangeFilterComponent } from '../grid-daterange-filter/grid-daterange-filter.component';
import { GridOptionsFilterComponent } from '../grid-options-filter/grid-options-filter.component';
import { SortChange } from '../grid-sorting/grid-sorting.component';
import { GridTypeaheadFilterComponent } from '../grid-typeahead-filter/grid-typeahead-filter.component';

export interface GridColumnValue {
  sortBy: string;
  sortDirection: SortDirection;
  filter: GridFilterValue;
}

@Component({
  selector: 'gcc-grid-header-cell',
  templateUrl: './grid-header-cell.component.html',
  styleUrls: ['./grid-header-cell.component.scss'],
  standalone: false
})
export class GridHeaderCellComponent extends WithSubscription() implements OnDestroy {
  @ViewChild('filterContainer', { read: ViewContainerRef }) public container: ViewContainerRef;
  @Input() public column: Column.GridColumn;
  @Input() public sortBy: string;
  @Input() public sortDirection: SortDirection;
  @Input()
  get filterValue(): GridFilterValue {
    return this.filter;
  }
  set filterValue(value: GridFilterValue) {
    this.filter = value || [];
  }

  @Output() public apply: EventEmitter<GridColumnValue> = new EventEmitter();

  public isOpen = false;

  public sortDirections = SortDirection;

  public columnValue: GridColumnValue;

  private componentRef: ComponentRef<BaseGridFilter>;
  private filter: GridFilterValue;
  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  public ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  public onSort({ sortBy, sortDirection }: SortChange) {
    this.columnValue.sortBy = sortBy;
    this.columnValue.sortDirection = sortDirection;
  }

  public onSortBy(sortBy: string) {
    if (this.sortBy !== sortBy) {
      this.apply.emit({ sortBy, sortDirection: SortDirection.Ascending, filter: this.filterValue });
    } else {
      this.apply.emit({
        sortBy,
        sortDirection:
          this.sortDirection === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending,
        filter: this.filterValue
      });
    }
  }

  public async onDropdownOpen(isOpen: boolean) {
    this.isOpen = isOpen;
    if (isOpen) {
      this.columnValue = {
        sortDirection: this.sortDirection,
        sortBy: this.sortBy,
        filter: this.filterValue
      };
      if (!this.componentRef) {
        setTimeout(() => {
          this.renderFilter();
        });
      } else {
        this.componentRef.instance.config = this.column.filter.config;
        this.componentRef.instance.value = this.columnValue.filter;
        this.componentRef.instance.name = this.column.name;
        this.cdr.markForCheck();
      }
    }
  }

  public onCancel() {
    this.isOpen = !this.isOpen;
  }
  public onApply() {
    this.apply.emit(this.columnValue);
    this.onCancel();
  }

  public onReset() {
    this.columnValue.filter = [];
    this.componentRef.instance.value = [];
    this.onApply();
  }

  private renderFilter() {
    this.container.clear();
    this.componentRef = this.container.createComponent(getFilterComponentByType(this.column.filter.type));

    this.componentRef.instance.config = this.column.filter.config;
    this.componentRef.instance.value = this.columnValue.filter;
    this.componentRef.instance.name = this.column.name;
    this.subs.add(this.componentRef.instance.valueChange.subscribe(value => (this.columnValue.filter = value)));
  }
}

export function getFilterComponentByType(type: ColumnFilterType): typeof BaseGridFilter {
  switch (type) {
    case ColumnFilterType.Options:
      return GridOptionsFilterComponent;
    case ColumnFilterType.Daterange:
      return GridDaterangeFilterComponent;
    case ColumnFilterType.Typeahead:
      return GridTypeaheadFilterComponent;
  }
}
